import { useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import DetailLayout from "@/components/admin/DetailLayout";
import StatusBadge from "@/components/admin/StatusBadge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Link2, Link2Off, Send, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

function relTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString("en-AU");
}

const STATUSES = ["new", "open", "pending_customer", "resolved", "closed"];

function tone(s) {
  if (s === "new") return "amber";
  if (s === "open") return "green";
  if (s === "pending_customer") return "amber";
  if (s === "resolved") return "green";
  if (s === "closed") return "grey";
  return "grey";
}

function StatusMenu({ current, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  return (
    <div className="relative" ref={ref}>
      <Button
        data-testid="action-status-menu"
        variant="outline"
        size="sm"
        onClick={() => setOpen((v) => !v)}
      >
        Change status ▾
      </Button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-20 bg-white border border-brand-linen shadow-lg min-w-[180px]" data-testid="status-menu">
          {STATUSES.map((s) => (
            <button
              key={s}
              data-testid={`status-option-${s}`}
              onClick={() => { setOpen(false); onChange(s); }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-brand-linen/30 ${current === s ? "font-semibold text-brand-teal" : ""}`}
            >
              {current === s ? "✓ " : ""}{s.replace(/_/g, " ")}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function AttachProjectDialog({ open, onClose, customerId, onPicked }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [showOther, setShowOther] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const params = new URLSearchParams({ page_size: "20", status: "all" });
    if (q) params.set("q", q);
    if (!showOther && customerId) params.set("customer_id", customerId);
    api.get(`/admin/projects?${params.toString()}`)
      .then((r) => setItems(r.data.items || []))
      .finally(() => setLoading(false));
  }, [open, q, showOther, customerId]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="dialog-attach-project" className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle data-testid="dialog-attach-project-title">Attach a project</DialogTitle>
          <DialogDescription>
            {customerId
              ? "Showing this customer's projects first. Toggle to see all customers."
              : "This ticket has no signed-in customer — pick any project."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input
            data-testid="attach-project-search"
            placeholder="Search title or owner email…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          {customerId && (
            <label className="flex items-center gap-2 text-xs text-brand-muted">
              <input
                data-testid="attach-project-other-customer-toggle"
                type="checkbox"
                checked={showOther}
                onChange={(e) => setShowOther(e.target.checked)}
              />
              Show projects from other customers
            </label>
          )}
          <div className="max-h-64 overflow-auto border border-brand-linen divide-y divide-brand-linen/40" data-testid="attach-project-list">
            {loading && <div className="p-3 text-xs text-brand-muted">Loading…</div>}
            {!loading && items.length === 0 && (
              <div className="p-3 text-xs text-brand-muted">No projects match.</div>
            )}
            {items.map((p) => (
              <button
                key={p.id}
                data-testid={`attach-project-option-${p.id}`}
                onClick={() => onPicked(p)}
                className="w-full text-left px-3 py-2 hover:bg-brand-linen/30"
              >
                <div className="text-sm text-brand-ink">{p.title}</div>
                <div className="text-xs text-brand-muted font-mono">{p.owner_email}</div>
              </button>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="dialog-attach-project-cancel" variant="outline" onClick={onClose}>Cancel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CrossCustomerConfirm({ open, project, onConfirm, onCancel, busy }) {
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent data-testid="dialog-cross-customer-confirm" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            Cross-customer project
          </DialogTitle>
          <DialogDescription data-testid="dialog-cross-customer-description">
            <span className="block">The project <strong>{project?.title}</strong> belongs to a different customer ({project?.owner_email}) than this ticket. This is allowed but unusual — it will be recorded in the audit log on both customers' timelines.</span>
            <span className="block mt-2">Are you sure you want to link this project?</span>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2">
          <Button data-testid="dialog-cross-customer-cancel" variant="outline" onClick={onCancel} disabled={busy}>Cancel</Button>
          <Button data-testid="dialog-cross-customer-confirm" onClick={onConfirm} disabled={busy} variant="destructive">
            {busy ? "Linking…" : "Yes — link cross-customer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminSupportDetail({ ticketId, onBack }) {
  const [t, setT] = useState(null);
  const [tab, setTab] = useState("conversation");
  const [err, setErr] = useState("");
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [crossConfirm, setCrossConfirm] = useState(null); // {project} | null
  const [crossBusy, setCrossBusy] = useState(false);

  const reload = useCallback(() => {
    api.get(`/admin/support/${ticketId}`)
      .then((r) => { setT(r.data); setErr(""); })
      .catch((e) => setErr(e.message || "Failed to load"));
  }, [ticketId]);

  useEffect(() => { reload(); }, [reload]);

  const sendReply = async () => {
    const body = reply.trim();
    if (body.length < 10) {
      toast.error("Reply must be at least 10 characters");
      return;
    }
    setSending(true);
    try {
      await api.post(`/admin/support/${ticketId}/reply`, { body });
      toast.success("Reply sent");
      setReply("");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send reply");
    } finally { setSending(false); }
  };

  const changeStatus = async (newStatus) => {
    if (!t || t.status === newStatus) return;
    try {
      await api.post(`/admin/support/${ticketId}/status`, { status: newStatus });
      toast.success(`Status: ${newStatus.replace(/_/g, " ")}`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to change status");
    }
  };

  const attachProject = async (project, confirmCross = false) => {
    try {
      const r = await api.post(`/admin/support/${ticketId}/attach-project`, {
        project_id: project.id,
        confirm_cross_customer: confirmCross,
      });
      toast.success(r.data.cross_customer ? "Project linked (cross-customer)" : "Project linked");
      setPickerOpen(false);
      setCrossConfirm(null);
      reload();
    } catch (e) {
      if (e?.response?.status === 409 && (e.response.data?.detail || "").includes("different customer")) {
        // Surface the cross-customer confirmation dialog
        setCrossConfirm({ project });
        setPickerOpen(false);
      } else {
        toast.error(e?.response?.data?.detail || "Failed to link project");
      }
    }
  };

  const detachProject = async () => {
    try {
      await api.post(`/admin/support/${ticketId}/detach-project`);
      toast.success("Project unlinked");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to detach");
    }
  };

  if (err) return <div className="text-sm border border-destructive/30 bg-destructive/5 text-destructive px-4 py-3">{err}</div>;
  if (!t) return <div className="text-sm text-brand-muted">Loading ticket…</div>;

  const isClosed = t.status === "closed";
  const tabs = [
    { key: "conversation", label: "Conversation", count: t.message_count },
    { key: "overview", label: "Overview" },
  ];

  const actions = (
    <>
      <StatusMenu current={t.status} onChange={changeStatus} />
      {t.project ? (
        <Button data-testid="action-detach-project" variant="outline" size="sm" onClick={detachProject}>
          <Link2Off className="w-4 h-4 mr-1" /> Detach project
        </Button>
      ) : (
        <Button data-testid="action-attach-project" variant="outline" size="sm" onClick={() => setPickerOpen(true)}>
          <Link2 className="w-4 h-4 mr-1" /> Attach project
        </Button>
      )}
    </>
  );

  let banner = null;
  if (t.status === "new") {
    banner = <div className="text-xs px-3 py-2 bg-blue-50 border border-blue-200 text-blue-700" data-testid="ticket-banner-new">Awaiting first reply</div>;
  } else if (t.status === "resolved") {
    banner = <div className="text-xs px-3 py-2 bg-brand-success/10 border border-brand-success/25 text-brand-success" data-testid="ticket-banner-resolved">Resolved · {relTime(t.last_activity_at)}</div>;
  } else if (t.status === "closed") {
    banner = <div className="text-xs px-3 py-2 bg-stone-100 border border-stone-300 text-stone-700" data-testid="ticket-banner-closed">Closed · {relTime(t.closed_at || t.updated_at)}</div>;
  }
  const crossBanner = t.project && t.project_cross_customer ? (
    <div className="text-xs px-3 py-2 bg-amber-50 border border-amber-200 text-amber-700" data-testid="ticket-banner-cross-customer">
      ⚠ Project linked is from a different customer than this ticket
    </div>
  ) : null;

  return (
    <>
      <DetailLayout
        testId="admin-ticket-detail"
        onBack={onBack}
        backLabel="← Back to support"
        title={t.subject}
        status={<StatusBadge tone={tone(t.status)} label={t.status} testId="ticket-status" />}
        subText={(
          <>
            From&nbsp;<span className="font-mono text-xs">{t.customer?.email}</span>
            &nbsp;· {relTime(t.created_at)} · {t.message_count} message{t.message_count === 1 ? "" : "s"}
            {t.project ? <> · Project: <span className="text-xs">{t.project.title}</span></> : null}
          </>
        )}
        actions={actions}
        banner={(
          <div className="space-y-2">
            {banner}
            {crossBanner}
          </div>
        )}
        tabs={tabs}
        activeTab={tab}
        onTabChange={setTab}
      >
        {tab === "conversation" && (
          <div data-testid="ticket-tab-conversation" className="space-y-4">
            <div className="bg-white border border-brand-linen p-4 space-y-3" data-testid="conversation-thread">
              {(t.messages || []).map((m) => (
                <div
                  key={m.id}
                  data-testid={`message-${m.id}`}
                  className={`flex ${m.from_kind === "admin" ? "justify-end" : "justify-start"}`}
                >
                  <div className={`max-w-[80%] px-3 py-2 ${m.from_kind === "admin" ? "bg-brand-navy text-white" : "bg-brand-linen/40 text-brand-ink"}`}>
                    <div className={`text-xs mb-1 ${m.from_kind === "admin" ? "text-white/80" : "text-brand-muted"}`}>
                      {m.from_email || m.from_kind} · {relTime(m.created_at)}
                    </div>
                    <div className="text-sm whitespace-pre-wrap">{m.body}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-white border border-brand-linen p-4">
              <div className="overline mb-2">Reply to customer</div>
              <Textarea
                data-testid="reply-composer-body"
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder={isClosed ? "Re-open this ticket before replying" : "Plain text reply. Will be emailed to the customer and saved here."}
                rows={4}
                disabled={isClosed}
                maxLength={8000}
              />
              <div className="flex items-center justify-between mt-2">
                <div className="text-xs text-brand-muted">{reply.length}/8000 · min 10 chars</div>
                <Button
                  data-testid="reply-composer-submit"
                  onClick={sendReply}
                  disabled={isClosed || sending || reply.trim().length < 10}
                >
                  <Send className="w-4 h-4 mr-1" />
                  {sending ? "Sending…" : "Send reply"}
                </Button>
              </div>
            </div>
          </div>
        )}

        {tab === "overview" && (
          <div data-testid="ticket-tab-overview" className="grid md:grid-cols-2 gap-6">
            <div className="bg-white border border-brand-linen p-4">
              <div className="overline mb-3">Ticket</div>
              <Row label="Subject" value={t.subject} />
              <Row label="Status" value={<StatusBadge tone={tone(t.status)} label={t.status} />} />
              <Row label="Created" value={t.created_at ? new Date(t.created_at).toLocaleString("en-AU") : "—"} />
              <Row label="Updated" value={relTime(t.updated_at)} />
              <Row label="Last activity by" value={t.last_activity_actor_kind} />
              {t.closed_at && <Row label="Closed" value={relTime(t.closed_at)} />}
            </div>
            <div className="bg-white border border-brand-linen p-4">
              <div className="overline mb-3">Customer & project</div>
              <Row label="Customer name" value={t.customer?.name} />
              <Row label="Customer email" mono value={t.customer?.email} />
              <Row label="Customer account" value={t.customer?.is_guest ? "Guest (not signed in)" : "Registered"} />
              <Row label="Project" value={t.project?.title || "—"} />
            </div>
          </div>
        )}
      </DetailLayout>

      <AttachProjectDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        customerId={t.customer?.id}
        onPicked={(p) => attachProject(p, false)}
      />
      <CrossCustomerConfirm
        open={!!crossConfirm}
        project={crossConfirm?.project}
        onConfirm={async () => {
          setCrossBusy(true);
          await attachProject(crossConfirm.project, true);
          setCrossBusy(false);
        }}
        onCancel={() => setCrossConfirm(null)}
        busy={crossBusy}
      />
    </>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5 border-b border-brand-linen/40 last:border-0">
      <div className="text-xs uppercase tracking-wider text-brand-muted">{label}</div>
      <div className={`text-sm text-brand-ink text-right ${mono ? "font-mono text-xs" : ""}`}>{value || "—"}</div>
    </div>
  );
}
